import { forwardRef, useState, type InputHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@shared/lib';
import { EyeIcon, EyeOffIcon } from '../Icon';

interface PasswordInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  invalid?: boolean;
}

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ invalid, className, ...props }, ref) => {
    const [revealed, setRevealed] = useState(false);
    const { t } = useTranslation();
    const Icon = revealed ? EyeOffIcon : EyeIcon;
    return (
      <div className={cn('relative', className)}>
        <input
          ref={ref}
          type={revealed ? 'text' : 'password'}
          className={cn(
            'w-full rounded-xl border bg-card py-3.5 pl-4 pr-12 text-sm text-fg outline-none transition-colors',
            'placeholder:text-muted-2',
            invalid ? 'border-danger focus:border-danger' : 'border-line focus:border-accent',
          )}
          {...props}
        />
        <button
          type="button"
          onClick={() => setRevealed((v) => !v)}
          aria-label={revealed ? t('shared.passwordInput.hide') : t('shared.passwordInput.show')}
          className="absolute right-1.5 top-1/2 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-[10px] text-muted transition-colors hover:bg-elev hover:text-fg"
        >
          <Icon className="h-4 w-4" />
        </button>
      </div>
    );
  },
);

PasswordInput.displayName = 'PasswordInput';
